import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const useProjectFinancials = (projectId: string | undefined) => {
  const { user } = useAuth();

  // Contract Value = sum of contracts.revised_value, fallback to approved JO amounts
  const contractValue = useQuery({
    queryKey: ["project_contract_value", projectId],
    queryFn: async () => {
      const { data: contracts, error: cErr } = await supabase
        .from("contracts")
        .select("id, revised_value")
        .eq("project_id", projectId!);
      if (cErr) throw cErr;
      if (!contracts?.length) {
        const { data, error } = await supabase
          .from("job_orders")
          .select("amount")
          .eq("project_id", projectId!)
          .in("status", ["approved", "closed"]);
        if (error) throw error;
        return data.reduce((s, r) => s + Number(r.amount), 0);
      }
      return contracts.reduce((s, c) => s + Number(c.revised_value), 0);
    },
    enabled: !!user && !!projectId,
  });

  // Estimated Cost = SOV-level: qty × (sub_unit_price + estimated_material_unit_cost)
  const estimatedCost = useQuery({
    queryKey: ["project_estimated_cost", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_project_estimated_cost", {
        p_project_id: projectId!,
      } as any);
      if (error) throw error;
      return Number(data ?? 0);
    },
    enabled: !!user && !!projectId,
  });

  // Committed Cost = canonical DB function (WO lines + approved POs)
  const committedCost = useQuery({
    queryKey: ["project_committed_cost", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_committed_cost", {
        p_project_id: projectId!,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
    enabled: !!user && !!projectId,
  });

  const billedToDate = useQuery({
    queryKey: ["project_billed_to_date", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("total")
        .eq("project_id", projectId!)
        .in("status", ["approved", "sent", "paid", "partially_paid", "overdue"] as any)
        .is("deleted_at", null);
      if (error) throw error;
      return data.reduce((s, r) => s + Number(r.total), 0);
    },
    enabled: !!user && !!projectId,
  });

  const collectedToDate = useQuery({
    queryKey: ["project_collected_to_date", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("amount_paid")
        .eq("project_id", projectId!)
        .is("deleted_at", null)
        .neq("status", "void" as any);
      if (error) throw error;
      return data.reduce((s, r) => s + Number(r.amount_paid ?? 0), 0);
    },
    enabled: !!user && !!projectId,
  });

  // Actual Cost: ALL approved project_expenses (attributed + unattributed)
  const actualCost = useQuery({
    queryKey: ["project_actual_cost", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_actual_cost", {
        p_project_id: projectId!,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
    enabled: !!user && !!projectId,
  });

  const cv = contractValue.data ?? 0;
  const ec = estimatedCost.data ?? 0;
  const cc = committedCost.data ?? 0;
  const ac = actualCost.data ?? 0;

  // Estimated Margin: based on SOV-level cost estimates
  const estimatedMargin = cv > 0 ? ((cv - ec) / cv) * 100 : 0;
  // Committed Margin
  const margin = cv > 0 ? ((cv - cc) / cv) * 100 : 0;
  // Actual Margin: uses TOTAL approved actual cost (all expenses, not just SOV-attributed)
  const actualCostMargin = cv > 0 ? ((cv - ac) / cv) * 100 : 0;

  return {
    contractValue: cv,
    estimatedCost: ec,
    committedCost: cc,
    actualCost: ac,
    billedToDate: billedToDate.data ?? 0,
    collectedToDate: collectedToDate.data ?? 0,
    estimatedMargin,
    margin,
    actualCostMargin,
    isLoading:
      contractValue.isLoading ||
      estimatedCost.isLoading ||
      committedCost.isLoading ||
      billedToDate.isLoading ||
      collectedToDate.isLoading ||
      actualCost.isLoading,
  };
};
